import type { TPerson } from '@/hooks/api/person';
import { buildTier, detectPlatform, getQueryStringParameter, sanitizeString } from './analytics.utils';
import type {
	TAnalyticsDataLayer,
	TAnalyticsEvent,
	TAnalyticsPagePayload,
	TAnalyticsSitePayload,
	TAnalyticsUserPayload
} from './analytics.types';

const buildSite = (): TAnalyticsSitePayload => ({
	brandOrigin: getQueryStringParameter('origem') ?? 'webmotors',
	domain: typeof window !== 'undefined' ? window.location.hostname ?? 'webmotors.com.br' : 'webmotors.com.br',
	country: 'brasil',
	server: 'web',
	environment: 'servicos',
	subEnvironment: 'garagem',
	clientType: 'pf',
	platform: detectPlatform()
});

const buildUser = (person: TPerson | undefined): TAnalyticsUserPayload | undefined => {
	if (!person) return undefined;
	const birthDate = person.birthDate
		? new Date(person.birthDate).toISOString().replace(/-/g, '/').substring(0, 10)
		: undefined;
	return {
		userID: String(person.id),
		loginStatus: '1',
		gender: person.gender === 'F' ? '2' : '1',
		birthDate,
		location: {
			city: sanitizeString(person.city),
			state: person.state?.toLowerCase()
		}
	};
};

const buildPage = (event: TAnalyticsEvent): TAnalyticsPagePayload => {
	const url = event.url ?? (typeof window !== 'undefined' ? window.location.href : '');
	const tierMap = buildTier(event.pageName, event.tier);
	return {
		pageName: event.pageName,
		pageType: event.pageType,
		flowType: event.flowType,
		url,
		trackingCode: {
			idcmp: getQueryStringParameter('idcmp'),
			idcmpint: getQueryStringParameter('idcmpint'),
			icid: getQueryStringParameter('icid'),
			lkid: getQueryStringParameter('lkid')
		},
		...(event.error ? { error: event.error } : {}),
		...(event.interaction ? { interaction: event.interaction } : {}),
		...tierMap
	} as TAnalyticsPagePayload;
};

/**
 * Monta o objDataLayer iterando pelo array `use` do evento e adicionando
 * apenas as seções pedidas (site/user/page) — espelha o `getObj` do
 * `withAdobeLaunch` da garagem.
 */
const buildDataLayer = (event: TAnalyticsEvent, person: TPerson | undefined): TAnalyticsDataLayer => {
	const dataLayer: TAnalyticsDataLayer = {};

	for (const scope of event.use) {
		switch (scope) {
			case 'site':
				dataLayer.site = buildSite();
				break;
			case 'user': {
				const user = buildUser(person);
				if (user) dataLayer.user = user;
				break;
			}
			case 'page':
				dataLayer.page = buildPage(event);
				break;
		}
	}

	return dataLayer;
};

/**
 * Verifica se todas as seções pedidas em `use` estão presentes no dataLayer.
 * Espelha o `isFinished` do `withAdobeLaunch` — eventos com dados parciais
 * não são disparados (ex: page view antes de TPerson carregar).
 */
const isDataLayerComplete = (event: TAnalyticsEvent, dataLayer: TAnalyticsDataLayer): boolean =>
	event.use.every(scope => dataLayer[scope] !== undefined);

export { buildDataLayer, isDataLayerComplete };
